import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Flag, Ban, ShieldOff, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'harassment', label: 'Harassment / abuse' },
  { value: 'scam_fraud', label: 'Scam / fraud' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'spam', label: 'Spam' },
  { value: 'no_show', label: 'No-show / unreliable' },
  { value: 'off_platform_payment', label: 'Off-platform payment' },
  { value: 'other', label: 'Other' },
];

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const ReportBlockControls = ({ targetUserId, targetName, threadId, bookingId, disabled = false, disabledHint }) => {
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [category, setCategory] = useState('harassment');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!targetUserId) return;
    axios.get(`${API}/users/${targetUserId}/relationship`, auth())
      .then((r) => setBlocked(r.data.i_blocked))
      .catch(() => {});
  }, [targetUserId]);

  const toggleBlock = async () => {
    setBusy(true);
    try {
      if (blocked) {
        await axios.delete(`${API}/users/${targetUserId}/block`, auth());
        setBlocked(false);
      } else {
        if (!window.confirm(`Block ${targetName || 'this user'}? You won't be able to message or book each other.`)) { setBusy(false); return; }
        await axios.post(`${API}/users/${targetUserId}/block`, {}, auth());
        setBlocked(true);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed. Please try again.');
    } finally { setBusy(false); }
  };

  const submitReport = async () => {
    if (reason.trim().length < 10) { alert('Please add at least a short explanation (10+ characters) so your report can be validated.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reports`, {
        reported_user_id: targetUserId, category, reason: reason.trim(),
        thread_id: threadId || null, booking_id: bookingId || null,
      }, auth());
      setDone(true);
      setTimeout(() => { setShowReport(false); setDone(false); setReason(''); }, 1600);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not submit report.');
    } finally { setSubmitting(false); }
  };

  if (!targetUserId) return null;

  return (
    <div className="flex items-center gap-2" data-testid="report-block-controls">
      <button onClick={() => setShowReport(true)} disabled={disabled}
        title={disabled ? disabledHint : undefined}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="report-user-btn">
        <Flag className="w-4 h-4" /> Report
      </button>
      <button onClick={toggleBlock} disabled={busy || disabled}
        title={disabled ? disabledHint : undefined}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${blocked ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100' : 'text-red-700 bg-red-50 hover:bg-red-100'}`}
        data-testid="block-user-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : blocked ? <ShieldOff className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
        {blocked ? 'Unblock' : 'Block'}
      </button>

      {showReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="report-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><Flag className="w-5 h-5 text-red-600" /> Report {targetName || 'user'}</h3>
              <button onClick={() => setShowReport(false)} className="text-gray-400 hover:text-gray-600" data-testid="report-close-btn"><X className="w-5 h-5" /></button>
            </div>
            {done ? (
              <div className="py-8 text-center text-emerald-700 font-medium" data-testid="report-success">✓ Report submitted. Our moderation team will review it.</div>
            ) : (
              <>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Reason</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg" data-testid="report-category-select">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Explanation <span className="text-red-500">*</span>
                  <span className="font-normal text-gray-400"> (required — reports without a clear explanation are marked invalid)</span>
                </label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
                  placeholder="Describe what happened, with any details/evidence (dates, amounts, screenshots you can share)…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none" data-testid="report-reason-input" />
                <button onClick={submitReport} disabled={submitting}
                  className="mt-4 w-full py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="report-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit report
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportBlockControls;
